/**
 * 极简紫韵模版 - 首页
 * 
 * 设计理念：非对称双栏Hero + 圆形图标分类云 + 横向轮播置顶 + 交替错落卡片
 * 与所有现有模板的布局骨架完全不同：
 * - Hero：左右非对称（左60%标题+搜索 / 右40%装饰图+统计）
 * - 分类：圆形图标云布局（flex-wrap，非网格非横向滚动）
 * - 置顶：横向大卡片轮播（overflow-x-auto snap）
 * - 最新：2列左-右-左-右交替错落高度卡片（非瀑布流/网格）
 * - 底部：三栏特色亮点卡片
 */

import { useEffect, useState, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import PurpleNavbar from "@/templates/purple/components/Navbar";
import PurpleFooter from "@/templates/purple/components/Footer";
import { getSiteInfo, getSiteCategories, getListings, type ListingItem, type FrontendCategory } from "@/utils/api";
import { useSite } from "@/context/SiteContext";

export default function PurpleHomePage() {
  const navigate = useNavigate();
  const { siteId, loading: siteLoading } = useSite();

  const [searchConfig, setSearchConfig] = useState({ enabled: true, disabledMessage: "" });
  const [categories, setCategories] = useState<FrontendCategory[]>([]);
  const [listings, setListings] = useState<ListingItem[]>([]);
  const [totalListings, setTotalListings] = useState(0);
  const [loadingListings, setLoadingListings] = useState(true);
  const [loadingCategories, setLoadingCategories] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [showSearchTip, setShowSearchTip] = useState(false);
  const pinnedScrollRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    getSiteInfo().then((info) => {
      if (info) {
        document.title = info.seo_title || '';
        if (info.search_enabled !== undefined) {
          setSearchConfig({
            enabled: info.search_enabled !== false,
            disabledMessage: info.search_disabled_message || "搜索功能已关闭",
          });
        }
      }
    });
  }, []);

  useEffect(() => {
    if (siteLoading || !siteId) return;
    getSiteCategories()
      .then((cats) => setCategories(cats.slice(0, 10)))
      .catch(console.error)
      .finally(() => setLoadingCategories(false));
  }, [siteId, siteLoading]);

  useEffect(() => {
    if (siteLoading || !siteId) return;
    getListings({ page_size: 14 })
      .then((data) => {
        setListings(data.list || []);
        setTotalListings(data.total || 0);
      })
      .catch(console.error)
      .finally(() => setLoadingListings(false));
  }, [siteId, siteLoading]);

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    if (!searchConfig.enabled) {
      setShowSearchTip(true);
      setTimeout(() => setShowSearchTip(false), 3000);
      return;
    }
    if (searchQuery.trim()) navigate(`/search?q=${encodeURIComponent(searchQuery.trim())}`);
  };

  const pinnedListings = listings.filter(l => l.is_pinned);
  const normalListings = listings.filter(l => !l.is_pinned);

  // 置顶轮播滚动
  const scrollPinned = (dir: 'left' | 'right') => {
    if (pinnedScrollRef.current) {
      pinnedScrollRef.current.scrollBy({ left: dir === 'left' ? -320 : 320, behavior: 'smooth' });
    }
  };

  // 特色亮点
  const highlights = [
    { icon: 'ri-shield-check-line', title: '安全可靠', desc: '信息严格审核，保障真实性', color: 'from-purple-100 to-violet-100', iconColor: 'text-purple-500' },
    { icon: 'ri-flashlight-line', title: '极速发布', desc: '一键发布，立即生效', color: 'from-fuchsia-100 to-pink-100', iconColor: 'text-fuchsia-500' },
    { icon: 'ri-customer-service-line', title: '专属客服', desc: 'VIP用户享受一对一服务', color: 'from-indigo-100 to-blue-100', iconColor: 'text-indigo-500' },
  ];

  return (
    <div className="min-h-screen bg-white">
      <PurpleNavbar />

      {/* ======== Hero：非对称双栏布局 ======== */}
      <section className="relative pt-24 pb-8 overflow-hidden">
        {/* 背景装饰 */}
        <div className="absolute inset-0 bg-gradient-to-br from-purple-50 via-violet-50/50 to-white" />
        <div className="absolute top-0 right-0 w-[500px] h-[500px] rounded-full bg-gradient-to-br from-purple-300/20 via-violet-300/10 to-fuchsia-300/10 blur-3xl" />
        <div className="absolute bottom-0 left-0 w-[400px] h-[400px] rounded-full bg-gradient-to-tr from-indigo-300/10 via-purple-300/5 to-violet-300/5 blur-3xl" />

        <div className="relative z-10 max-w-6xl mx-auto px-4 sm:px-6">
          <div className="flex flex-col lg:flex-row items-center gap-10 lg:gap-16">
            {/* 左栏 60% */}
            <div className="lg:w-3/5 text-center lg:text-left">
              <div className="inline-flex items-center gap-2 px-4 py-1.5 bg-purple-100/80 rounded-full text-purple-700 text-xs font-semibold mb-5">
                <i className="ri-sparkling-line" />
                简洁 · 高效 · 专业
              </div>
              <h1 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold text-gray-900 leading-tight mb-4">
                精准触达，让每一份
                <span className="bg-gradient-to-r from-purple-600 via-violet-600 to-fuchsia-600 bg-clip-text text-transparent"> 信息</span>都找到归属
              </h1>
              <p className="text-gray-500 text-base sm:text-lg mb-8 max-w-xl lg:max-w-none">
                干净高效的分类信息平台，一键发布，精准搜索
              </p>

              {/* 搜索框 */}
              <form onSubmit={handleSearch} className="max-w-xl">
                <div className="relative group">
                  <div className="absolute inset-0 bg-gradient-to-r from-purple-400 to-violet-500 rounded-2xl blur-md opacity-20 group-hover:opacity-30 transition-opacity" />
                  <div className="relative flex items-center bg-white rounded-2xl border border-purple-200 group-hover:border-purple-400 shadow-lg shadow-purple-500/5 transition-all overflow-hidden">
                    <div className="flex-1 flex items-center pl-5">
                      <i className="ri-search-line text-purple-400 text-lg mr-3" />
                      <input
                        type="text"
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        placeholder="搜索关键词..."
                        className="flex-1 py-4 text-base text-gray-700 placeholder-gray-400 focus:outline-none bg-transparent"
                      />
                    </div>
                    <button
                      type="submit"
                      className="shrink-0 px-8 py-4 bg-gradient-to-r from-purple-600 to-violet-600 text-white font-bold hover:from-purple-700 hover:to-violet-700 transition-all text-sm"
                    >
                      <i className="ri-search-line mr-1" />搜索
                    </button>
                  </div>
                  {showSearchTip && (
                    <div className="absolute left-1/2 -translate-x-1/2 top-full mt-3 px-4 py-2.5 bg-purple-500 text-white text-sm rounded-xl shadow-lg whitespace-nowrap z-10">
                      <i className="ri-error-warning-line mr-1" />{searchConfig.disabledMessage || "搜索功能已关闭"}
                    </div>
                  )}
                </div>
              </form>


            </div>

            {/* 右栏 40%：装饰插画卡片 */}
            <div className="lg:w-2/5 relative hidden lg:block">
              <div className="relative">
                {/* 浮动卡片 */}
                <div className="absolute -top-6 -left-6 w-32 h-32 rounded-2xl bg-white/80 backdrop-blur-sm shadow-xl border border-purple-100 rotate-6 flex items-center justify-center">
                  <div className="text-center">
                    <div className="w-10 h-10 mx-auto rounded-xl bg-gradient-to-br from-purple-400 to-violet-500 flex items-center justify-center">
                      <i className="ri-file-list-line text-white text-lg" />
                    </div>
                    <p className="text-[10px] font-semibold text-gray-600 mt-2">发布管理</p>
                  </div>
                </div>
                <div className="absolute top-16 right-0 w-28 h-28 rounded-2xl bg-white/80 backdrop-blur-sm shadow-xl border border-violet-100 -rotate-3 flex items-center justify-center">
                  <div className="text-center">
                    <div className="w-10 h-10 mx-auto rounded-xl bg-gradient-to-br from-fuchsia-400 to-pink-500 flex items-center justify-center">
                      <i className="ri-search-line text-white text-lg" />
                    </div>
                    <p className="text-[10px] font-semibold text-gray-600 mt-2">智能搜索</p>
                  </div>
                </div>


                {/* 中央大图 */}
                <div className="w-64 h-80 mx-auto rounded-3xl bg-gradient-to-br from-purple-200 via-violet-200 to-fuchsia-200 flex items-center justify-center shadow-2xl shadow-purple-200/50">
                  <div className="text-center">
                    <div className="w-20 h-20 mx-auto rounded-3xl bg-white/80 backdrop-blur-sm flex items-center justify-center shadow-lg">
                      <i className="ri-building-line text-4xl text-purple-500" />
                    </div>
                    <p className="text-sm font-bold text-purple-700 mt-4">分类信息</p>
                    <p className="text-xs text-purple-500 mt-1">一站式平台</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ======== 分类：圆形图标云 ======== */}
      <section className="pb-10">
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <div className="text-center mb-8">
            <h2 className="text-xl font-bold text-gray-800">浏览分类</h2>
            <p className="text-sm text-gray-400 mt-1">选择你感兴趣的领域</p>
          </div>

          {loadingCategories ? (
            <div className="flex flex-wrap justify-center gap-4">
              {[...Array(8)].map((_, i) => (
                <div key={i} className="animate-pulse w-24 h-24 bg-gray-100 rounded-full" />
              ))}
            </div>
          ) : (
            <div className="grid grid-cols-4 gap-6 max-w-3xl mx-auto place-items-center">
              {categories.map((cat) => (
                <Link
                  key={cat.id}
                  to={`/category/${cat.slug}`}
                  className="w-full aspect-square max-w-[100px] rounded-full bg-white border-2 border-purple-100 hover:border-purple-400 hover:shadow-lg hover:shadow-purple-500/10 hover:-translate-y-1 transition-all duration-300 flex flex-col items-center justify-center gap-1 group"
                >
                  <i className={`${cat.icon || 'ri-folder-line'} text-xl text-purple-400 group-hover:text-purple-600 transition-colors`} />
                  <span className="text-[10px] sm:text-xs text-gray-500 group-hover:text-purple-700 font-medium text-center line-clamp-1 px-1">
                    {cat.name}
                  </span>
                </Link>
              ))}
              <Link
                to="/category"
                className="w-full aspect-square max-w-[100px] rounded-full bg-gradient-to-br from-purple-50 to-violet-50 border-2 border-purple-200 border-dashed hover:border-solid hover:bg-purple-100 transition-all flex flex-col items-center justify-center gap-1 group"
              >
                <i className="ri-more-line text-xl text-purple-400" />
                <span className="text-[10px] text-purple-500 font-medium">更多</span>
              </Link>
            </div>
          )}
        </div>
      </section>

      {/* ======== 置顶推荐：横向卡片轮播 ======== */}
      {!loadingListings && pinnedListings.length > 0 && (
        <section className="pb-12">
          <div className="max-w-6xl mx-auto px-4 sm:px-6">
            <div className="flex items-center justify-between mb-5">
              <div className="flex items-center gap-2">
                <div className="w-1 h-5 bg-purple-500 rounded-full" />
                <h2 className="text-lg font-bold text-gray-800">精选推荐</h2>
                <span className="text-xs px-2.5 py-0.5 bg-purple-100 text-purple-600 rounded-full font-medium">
                  <i className="ri-pushpin-line text-[10px] mr-1" />置顶
                </span>
              </div>
              <div className="flex items-center gap-2">
                <button onClick={() => scrollPinned('left')} className="w-9 h-9 rounded-xl bg-white border border-gray-200 flex items-center justify-center hover:border-purple-300 hover:text-purple-600 transition-all text-gray-400">
                  <i className="ri-arrow-left-s-line" />
                </button>
                <button onClick={() => scrollPinned('right')} className="w-9 h-9 rounded-xl bg-white border border-gray-200 flex items-center justify-center hover:border-purple-300 hover:text-purple-600 transition-all text-gray-400">
                  <i className="ri-arrow-right-s-line" />
                </button>
              </div>
            </div>

            <div
              ref={pinnedScrollRef}
              className="flex gap-4 overflow-x-auto pb-2 scrollbar-hide snap-x snap-mandatory"
            >
              {pinnedListings.map((item, idx) => (
                <Link
                  key={item.id}
                  to={`/listing/${item.id}`}
                  className={`shrink-0 snap-start group ${idx === 0 ? 'w-72 sm:w-80' : 'w-56 sm:w-64'}`}
                >
                  <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden hover:shadow-xl hover:border-purple-200 transition-all h-full flex flex-col">
                    <div className={`${idx === 0 ? 'aspect-[4/3]' : 'aspect-square'} bg-purple-50 overflow-hidden`}>
                      {item.images?.[0] ? (
                        <img src={item.images[0]} alt="" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center">
                          <i className="ri-image-line text-purple-300 text-3xl" />
                        </div>
                      )}
                      <span className="absolute top-3 left-3 text-[10px] px-2 py-0.5 rounded-full bg-purple-500 text-white font-bold shadow-sm">
                        推荐
                      </span>
                    </div>
                    <div className="p-4 flex-1 flex flex-col justify-between">
                      <h3 className="text-sm font-semibold text-gray-800 line-clamp-2 group-hover:text-purple-600 transition-colors leading-snug">
                        {item.title}
                      </h3>
                      {idx === 0 && (
                        <p className="text-xs text-gray-400 mt-2 line-clamp-2">
                          {item.content?.substring(0, 80) || "暂无描述"}
                        </p>
                      )}
                      <div className="flex items-center gap-2 mt-3 text-xs text-gray-400">
                        <span>{item.category?.name || "未分类"}</span>
                        <span>·</span>
                        <span className="flex items-center gap-1"><i className="ri-eye-line" />{item.view_count || 0}</span>
                      </div>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* ======== 最新信息：交替错落2列布局 ======== */}
      <section className="pb-14">
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <div className="flex items-center justify-between mb-5">
            <div className="flex items-center gap-2">
              <div className="w-1 h-5 bg-violet-500 rounded-full" />
              <h2 className="text-lg font-bold text-gray-800">最新发布</h2>
            </div>
            <Link to="/category" className="text-sm text-purple-600 hover:text-purple-700 font-medium flex items-center gap-1">
              查看全部 <i className="ri-arrow-right-line" />
            </Link>
          </div>

          {loadingListings ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {[...Array(6)].map((_, i) => (
                <div key={i} className="animate-pulse bg-white rounded-2xl border border-gray-100 overflow-hidden">
                  <div className="aspect-[16/9] bg-gray-200" />
                  <div className="p-5 space-y-2">
                    <div className="w-3/4 h-4 bg-gray-200 rounded" />
                    <div className="w-full h-3 bg-gray-100 rounded" />
                    <div className="w-1/2 h-3 bg-gray-100 rounded" />
                  </div>
                </div>
              ))}
            </div>
          ) : normalListings.length > 0 ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {normalListings.slice(0, 10).map((item, idx) => {
                // 交替错落：偶数行高卡片，奇数行矮卡片
                const isTall = idx % 3 === 0;
                return (
                  <Link
                    key={item.id}
                    to={`/listing/${item.id}`}
                    className={`group bg-white rounded-2xl border border-gray-100 overflow-hidden hover:shadow-lg hover:border-purple-200 transition-all ${
                      isTall ? 'sm:row-span-2' : ''
                    }`}
                  >
                    {/* 统一使用正方形图片 */}
                    <div className="aspect-square bg-purple-50 overflow-hidden relative">
                      {item.images?.[0] ? (
                        <img
                          src={item.images[0]}
                          alt={item.title}
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-400"
                        />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center">
                          <i className="ri-image-line text-purple-300 text-3xl" />
                        </div>
                      )}
                    </div>
                    <div className="p-4 sm:p-5">
                      <h3 className="text-sm font-semibold text-gray-800 line-clamp-2 group-hover:text-purple-600 transition-colors leading-snug">
                        {item.title}
                      </h3>
                      {/* 高卡片多显示描述 */}
                      {isTall && (
                        <p className="text-xs text-gray-400 mt-2 line-clamp-3 leading-relaxed">
                          {item.content?.substring(0, 120) || "暂无描述"}
                        </p>
                      )}
                      <div className="flex items-center justify-between mt-3 pt-3 border-t border-gray-50">
                        <span className="text-[11px] px-2.5 py-0.5 bg-purple-50 text-purple-600 rounded-full font-medium">
                          {item.category?.name || "未分类"}
                        </span>
                        <span className="text-[11px] text-gray-400 flex items-center gap-1">
                          <i className="ri-time-line" />
                          {formatDate(item.created_at)}
                        </span>
                      </div>
                    </div>
                  </Link>
                );
              })}
            </div>
          ) : (
            <div className="text-center py-16 bg-white rounded-3xl border border-gray-100">
              <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center rounded-2xl bg-purple-50">
                <i className="ri-inbox-line text-2xl text-purple-400" />
              </div>
              <p className="text-gray-500 font-medium">暂无信息</p>
              <p className="text-gray-400 text-sm mt-1">快来发布第一条信息吧</p>
              <Link to="/publish" className="inline-flex items-center gap-2 mt-5 px-6 py-3 bg-gradient-to-r from-purple-600 to-violet-600 text-white text-sm font-bold rounded-2xl hover:from-purple-700 hover:to-violet-700 transition-all shadow-md">
                <i className="ri-add-line" />立即发布
              </Link>
            </div>
          )}
        </div>
      </section>

      {/* ======== 特色亮点 ======== */}
      <section className="pb-16">
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            {highlights.map((item, idx) => (
              <div
                key={idx}
                className={`group p-6 bg-gradient-to-br ${item.color} rounded-2xl border border-purple-100/50 hover:shadow-lg hover:-translate-y-1 transition-all duration-300`}
              >
                <div className={`w-11 h-11 rounded-xl bg-white flex items-center justify-center mb-4 shadow-sm group-hover:scale-110 transition-transform`}>
                  <i className={`${item.icon} ${item.iconColor} text-xl`} />
                </div>
                <h3 className="text-sm font-bold text-gray-800 mb-1">{item.title}</h3>
                <p className="text-xs text-gray-500">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <PurpleFooter />
    </div>
  );
}

function formatDate(dateStr: string): string {
  const d = new Date(dateStr);
  const now = new Date();
  const diff = now.getTime() - d.getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 60) return `${mins}分钟前`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}小时前`;
  const days = Math.floor(hours / 24);
  if (days < 7) return `${days}天前`;
  return `${d.getMonth() + 1}-${d.getDate()}`;
}
